import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Button, Chip, CircularProgress, IconButton, Collapse,
  Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete,
  TextField, Avatar
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import PendingIcon from '@mui/icons-material/Pending';
import HistoryIcon from '@mui/icons-material/History';
import API_ENDPOINTS from '../config/apiConfig';
import { useTranslation } from '../i18n/i18n';
import FixedSnackbar from './FixedSnackbar';

export default function SupervisorVacationReview() {
  const { t } = useTranslation();
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [selectedVac, setSelectedVac] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [filterEmail, setFilterEmail] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pendRes, histRes] = await Promise.all([
        fetch(API_ENDPOINTS.VACATION_PENDING, { credentials: 'include' }),
        fetch(API_ENDPOINTS.VACATION_HISTORY, { credentials: 'include' })
      ]);
      if (pendRes.ok) {
        const data = await pendRes.json();
        setPending(data.vacations || []);
      }
      if (histRes.ok) {
        const data = await histRes.json();
        setHistory(data.vacations || []);
      }
    } catch (err) {
      console.error('Error fetching vacations:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const employeeOptions = useMemo(() => {
    const map = new Map();
    [...pending, ...history].forEach((v) => {
      if (!map.has(v.user_email)) {
        map.set(v.user_email, { email: v.user_email, fullname: v.fullname || v.user_email });
      }
    });
    return Array.from(map.values());
  }, [pending, history]);

  const filteredPending = useMemo(() => {
    if (!filterEmail) return pending;
    return pending.filter((v) => v.user_email === filterEmail);
  }, [pending, filterEmail]);

  const filteredHistory = useMemo(() => {
    if (!filterEmail) return history;
    return history.filter((v) => v.user_email === filterEmail);
  }, [history, filterEmail]);

  const handleApprove = async (vac) => {
    setSelectedVac(vac);
    setApproveDialogOpen(true);
  };

  const confirmApprove = async () => {
    if (!selectedVac) return;
    setActionLoading(true);
    try {
      const res = await fetch(API_ENDPOINTS.VACATION_APPROVE(selectedVac._id), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'vacation_supervisor_rejected');
      }
      setSnackbar({ open: true, message: t('vacation_supervisor_approved'), severity: 'success' });
      setApproveDialogOpen(false);
      setSelectedVac(null);
      fetchAll();
    } catch (err) {
      setSnackbar({ open: true, message: t(err.message) || t('vacation_supervisor_rejected'), severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedVac) return;
    setActionLoading(true);
    try {
      const res = await fetch(API_ENDPOINTS.VACATION_REJECT(selectedVac._id), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason: rejectReason })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'vacation_supervisor_rejected');
      }
      setSnackbar({ open: true, message: t('vacation_supervisor_rejected'), severity: 'success' });
      setRejectDialogOpen(false);
      setSelectedVac(null);
      setRejectReason('');
      fetchAll();
    } catch (err) {
      setSnackbar({ open: true, message: t(err.message) || t('vacation_supervisor_rejected'), severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString();
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading vacation requests...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Employee Filter */}
      <Autocomplete
        options={employeeOptions}
        getOptionLabel={(opt) => opt.fullname || opt.email}
        value={filterEmail ? employeeOptions.find((o) => o.email === filterEmail) || null : null}
        onChange={(_, val) => setFilterEmail(val ? val.email : null)}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('vacation_supervisor_filter')}
            size="small"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <FilterListIcon fontSize="small" sx={{ color: '#62B6CB', mr: 0.5 }} />
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
          />
        )}
        sx={{ mb: 3, minWidth: { xs: '100%', sm: 200 }, maxWidth: 350 }}
        clearOnEscape
      />

      {/* Pending Section */}
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 2, border: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Avatar sx={{ width: 36, height: 36, bgcolor: 'warning.main', color: 'warning.contrastText' }}>
            <PendingIcon sx={{ fontSize: 20 }} />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('vacation_supervisor_pending')}
          </Typography>
          {filteredPending.length > 0 && (
            <Chip label={filteredPending.length} size="small" sx={{ height: 24, fontWeight: 500 }} />
          )}
        </Box>

        {filteredPending.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              {t('vacation_supervisor_no_pending')}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ minWidth: 560 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>{t('name') || 'Name'}</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>{t('vacation_type')}</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>{t('vacation_start_date')}</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>{t('vacation_end_date')}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>{t('status')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>{t('actions') || 'Actions'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPending.map((v) => (
                  <React.Fragment key={v._id}>
                    <TableRow
                      hover
                      sx={{ cursor: 'pointer', '& .MuiTableCell-root': { py: 1.5 } }}
                      onClick={() => setExpanded(expanded === v._id ? null : v._id)}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{v.fullname || v.user_email}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {v.type === 'vacation' ? t('vacation_type_vacation') : t('vacation_type_day_off')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatDate(v.start_date)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatDate(v.end_date)}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={t('vacation_status_pending')}
                          size="small"
                          color="warning"
                          sx={{
                            height: 24,
                            fontSize: '0.75rem',
                            fontWeight: 500
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          color="success"
                          variant="contained"
                          startIcon={actionLoading ? <CircularProgress size={14} /> : <CheckCircleIcon />}
                          onClick={(e) => { e.stopPropagation(); handleApprove(v); }}
                          disabled={actionLoading}
                          sx={{ mr: 1, textTransform: 'none', boxShadow: 'none' }}
                        >
                          {t('calendar_supervisor_approve')}
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          startIcon={<CancelIcon />}
                          onClick={(e) => { e.stopPropagation(); setSelectedVac(v); setRejectDialogOpen(true); }}
                          disabled={actionLoading}
                          sx={{ textTransform: 'none' }}
                        >
                          {t('calendar_supervisor_reject')}
                        </Button>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={6} sx={{ py: 0, border: 0 }}>
                        <Collapse in={expanded === v._id}>
                          <Box sx={{ py: 2, px: 1 }}>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              {t('vacation_days_count', { n: v.days_count })}
                            </Typography>
                            {v.reason && (
                              <Typography variant="body2" color="text.secondary">
                                {t('vacation_reason')}: {v.reason}
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* History Section */}
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 2, border: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Avatar sx={{ width: 36, height: 36, bgcolor: 'grey.100', color: 'grey.600' }}>
            <HistoryIcon sx={{ fontSize: 20 }} />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('vacation_supervisor_history')}
          </Typography>
          {filteredHistory.length > 0 && (
            <Chip label={filteredHistory.length} size="small" sx={{ height: 24, fontWeight: 500 }} />
          )}
        </Box>

        {filteredHistory.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              {t('vacation_supervisor_no_history')}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ minWidth: 560 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>{t('name') || 'Name'}</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>{t('vacation_type')}</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>{t('vacation_start_date')}</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>{t('vacation_end_date')}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>{t('status')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>{t('calendar_notes_label')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredHistory.map((v) => {
                  const statusColor = { approved: 'success', rejected: 'error' };
                  return (
                    <React.Fragment key={v._id}>
                      <TableRow
                        hover
                        sx={{ cursor: 'pointer', '& .MuiTableCell-root': { py: 1.5 } }}
                        onClick={() => setExpanded(expanded === v._id ? null : v._id)}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{v.fullname || v.user_email}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {v.type === 'vacation' ? t('vacation_type_vacation') : t('vacation_type_day_off')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatDate(v.start_date)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatDate(v.end_date)}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={t(`vacation_status_${v.status}`)}
                            size="small"
                            color={statusColor[v.status] || 'default'}
                            sx={{
                              height: 24,
                              fontSize: '0.75rem',
                              fontWeight: 500
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small">
                            {expanded === v._id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={6} sx={{ py: 0, border: 0 }}>
                          <Collapse in={expanded === v._id}>
                            <Box sx={{ py: 2, px: 1 }}>
                              <Typography variant="body2" sx={{ mb: 0.5 }}>
                                {t('vacation_days_count', { n: v.days_count })}
                              </Typography>
                              {v.reason && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                  {t('vacation_reason')}: {v.reason}
                                </Typography>
                              )}
                              {v.status === 'rejected' && v.rejection_reason && (
                                <Typography variant="body2" color="error">
                                  {t('calendar_rejection_reason')}: {v.rejection_reason}
                                </Typography>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('calendar_supervisor_rejection_dialog')}
          </Typography>
          <IconButton onClick={() => { setRejectDialogOpen(false); setSelectedVac(null); setRejectReason(''); }} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('calendar_supervisor_rejection_prompt')}
          </Typography>
          <TextField
            autoFocus
            multiline
            rows={3}
            fullWidth
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: 1.5 }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button
            onClick={() => { setRejectDialogOpen(false); setSelectedVac(null); setRejectReason(''); }}
            sx={{ textTransform: 'none' }}
          >
            {t('cancel') || 'Cancel'}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={actionLoading}
            sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
          >
            {actionLoading ? <CircularProgress size={18} /> : t('calendar_supervisor_reject')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve Confirmation Dialog */}
      <Dialog open={approveDialogOpen} onClose={() => { setApproveDialogOpen(false); setSelectedVac(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('calendar_supervisor_approve') || 'Approve Vacation'}
          </Typography>
          <IconButton onClick={() => { setApproveDialogOpen(false); setSelectedVac(null); }} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {t('vacation_supervisor_approve_confirm') || 'Are you sure you want to approve this vacation request?'}
          </Typography>
          {selectedVac && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {selectedVac.fullname || selectedVac.user_email} — {formatDate(selectedVac.start_date)} – {formatDate(selectedVac.end_date)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={() => { setApproveDialogOpen(false); setSelectedVac(null); }} sx={{ textTransform: 'none' }}>
            {t('cancel') || 'Cancel'}
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={confirmApprove}
            disabled={actionLoading}
            sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
          >
            {actionLoading ? <CircularProgress size={18} /> : t('calendar_supervisor_approve')}
          </Button>
        </DialogActions>
      </Dialog>

      <FixedSnackbar
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        severity={snackbar.severity}
        autoHideDuration={4000}
      />
    </Box>
  );
}
